/* eslint max-lines: ["error", {max: 331}] -- Due to long file */
import type { BaseQueryApi } from "@reduxjs/toolkit/query";
import { isNil } from "lodash";
import {
  CredentialsManagerError,
  CredentialsManagerErrorCodes,
  MfaError,
  WebAuthError,
} from "react-native-auth0";
import type { AuthError } from "react-native-auth0";

import { FailedLoginReason } from "@/constants/analytics/Login";
import type {
  AuthErrorType,
  CredentialsManagerErrorType,
  SerializableAuthError,
} from "@/constants/Auth0Errors";
import {
  AuthErrorCode,
  AuthErrorCodeToFailedLoginReason,
  AuthErrorCodeToMessage,
  AuthErrorSeverityByCode,
  AuthErrorSeverityByType,
  AuthErrorTypeToMessage,
  Severity,
} from "@/constants/Auth0Errors";
import { LoginErrors } from "@/constants/Login";
import type { ErrorSource } from "@/crossPlatform/Datadog";
import { captureException, captureMessage } from "@/services/AppMonitor";
import type { AppStartupSliceState } from "@/store/reducers/appStartup";
import { setAuth0RenewFailureAction } from "@/store/reducers/appStartup";
import type { AuthSliceState } from "@/store/reducers/auth";
import { setShouldPreventApiCallsDueToExpiredTokenAction } from "@/store/reducers/auth";
import { setOrphanToastRequestAction } from "@/store/reducers/toast";
import { isAndroid } from "@/utils/platform.util";

type AuthErrorJson = {
  mfa_token?: string;
};

const isCredentialsManagerErrorOfType = ({
  error,
  type,
}: {
  error: unknown;
  type: CredentialsManagerErrorType;
}) => {
  return error instanceof CredentialsManagerError && error.type === type;
};

const isUnrecoverableCredentialsErrorOnApiCall = (error: unknown) =>
  isCredentialsManagerErrorOfType({
    error,
    type: CredentialsManagerErrorCodes.RENEW_FAILED,
  }) ||
  isCredentialsManagerErrorOfType({
    error,
    type: CredentialsManagerErrorCodes.NO_REFRESH_TOKEN,
  });

const isUnrecoverableCredentialsErrorOnCheckCreds = (
  error: SerializableAuthError,
) =>
  // by default, if the Auth0 SDK has no credentials, that's not an error
  // so if this is thrown as an error, it means things are in a bad/invalid state
  error.type === CredentialsManagerErrorCodes.NO_CREDENTIALS ||
  error.type === CredentialsManagerErrorCodes.NO_REFRESH_TOKEN ||
  error.type === CredentialsManagerErrorCodes.RENEW_FAILED;

const isUnrecoverableCredentialsErrorOnAppInit = (
  error: SerializableAuthError,
) =>
  error.code === AuthErrorCode.InvalidGrant ||
  error.code === AuthErrorCode.InvalidState ||
  error.code === AuthErrorCode.MissingTransaction;

const serializeAuthError = (
  error: unknown,
): { error: SerializableAuthError; json?: AuthErrorJson } => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const authError = error as
    | AuthError
    | CredentialsManagerError
    | MfaError
    | WebAuthError;
  const serializableError: SerializableAuthError = {
    code: authError.code,
    message: authError.message,
    name: authError.name,
    status: authError.status,
  };
  if (
    authError instanceof CredentialsManagerError ||
    authError instanceof MfaError ||
    authError instanceof WebAuthError
  ) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    serializableError.type = authError.type as AuthErrorType;
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return { error: serializableError, json: authError.json as AuthErrorJson };
};

const getFailedLoginReason = (error: SerializableAuthError) =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  AuthErrorCodeToFailedLoginReason[error.code as AuthErrorCode] ??
  FailedLoginReason.Other;

type SerializableAuthResponse<T> =
  | {
      data: T;
      error?: never;
      json?: never;
    }
  | { data?: never; error: SerializableAuthError; json?: AuthErrorJson };

const getUserFacingLoginErrorResponse = (
  error: SerializableAuthError,
): SerializableAuthResponse<never> => {
  let message =
    (error.type && AuthErrorTypeToMessage[error.type]) ??
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    AuthErrorCodeToMessage[error.code as AuthErrorCode] ??
    LoginErrors.Default;

  // invalid username/password vs. invalid MFA have the same error code,
  // and are only disambiguated by the error message
  if (
    error.code === AuthErrorCode.InvalidGrant &&
    error.message.includes("binding_code")
  ) {
    message = LoginErrors.InvalidMFA;
  }

  return {
    error: {
      ...error,
      message,
    },
  };
};

const captureAuthErrorMessage = ({
  error,
  errorSource,
  extra,
  scenario,
}: {
  error: SerializableAuthError;
  errorSource?: ErrorSource;
  extra?: { [key: string]: unknown };
  scenario?: string;
}) => {
  const { message, ...rest } = error;
  const context = { extra, scenario, ...rest };
  captureMessage(message, { context, errorSource });
};

const handleAuthError = ({
  context: extra,
  error,
  errorSource,
  scenario,
}: {
  context?: { [key: string]: unknown };
  error: unknown;
  errorSource?: ErrorSource;
  scenario?: string;
}) => {
  const { error: serializableError, json } = serializeAuthError(error);
  const { code, message, type, ...rest } = serializableError;

  const severity =
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    AuthErrorSeverityByCode[code as AuthErrorCode] ??
    (type && AuthErrorSeverityByType[type]);

  switch (severity) {
    case Severity.Exception:
    case undefined:
      captureException(new Error(message), {
        context: {
          code,
          extra,
          scenario,
          type,
          ...rest,
        },
        errorSource,
      });
      break;
    case Severity.Message:
      captureAuthErrorMessage({
        error: serializableError,
        errorSource,
        extra,
        scenario,
      });
      break;
    case Severity.Silent:
      break;
  }
  return { error: serializableError, json };
};

const wrapAuth0Call = async <T = void>({
  auth0Call,
  errorContext,
  errorScenario,
}: {
  auth0Call: () => Promise<T>;
  errorContext?: { [key: string]: unknown };
  errorScenario?: string;
}): Promise<SerializableAuthResponse<T>> => {
  try {
    const data = await auth0Call();
    return { data };
  } catch (error) {
    return handleAuthError({
      context: errorContext,
      error,
      scenario: errorScenario,
    });
  }
};

/**
 * Returns the appropriate biometrics label based on the platform.
 */
const getBiometricsLabel = ({
  isFaceRecognitionSupported,
}: {
  isFaceRecognitionSupported: boolean;
}): string => {
  if (isAndroid()) {
    return isFaceRecognitionSupported ? "Face Unlock" : "Fingerprint Unlock";
  }
  return isFaceRecognitionSupported ? "Enable Face ID" : "Enable Touch ID";
};

const Auth0SessionExpiredToastText =
  "A technical problem has occurred. Please log out and log back in.";

const handleUnrecoverableCredentialsApiCallErrorWeb = ({
  api,
}: {
  api: BaseQueryApi;
  error: unknown;
}): void => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  const state = api.getState() as { auth: AuthSliceState };

  // if already handled, do not run this again
  if (state.auth.shouldPreventApiCallsDueToExpiredToken) return;

  // toast shown once per web-app session, as soon as an API call first detects an Auth0 Unrecoverable error
  api.dispatch(
    setOrphanToastRequestAction({
      text: Auth0SessionExpiredToastText,
      variant: "error",
    }),
  );

  // this redux value being set will:
  //  1. prevent the baseQuery from making further API calls with an expired token (doomed to fail)
  //  2. cause useHandleAuthExpiration's useEffect to auto-logout the user, only once they perform a navigation event
  //     (mirrors my-equip-v1 behavior of expiration-related logout only on subsequent navigation)

  // additionally, since the mobile app, unlike my-equip-v1, has video content
  // that is much longer than the Auth0 5minute hard limit. this ensures that users
  // watching a long video are not auto-logged-out mid video; only possibly shown a toast (if an API call is made)
  api.dispatch(setShouldPreventApiCallsDueToExpiredTokenAction(true));
};

const handleUnrecoverableCredentialsApiCallErrorNative = ({
  api,
  error,
}: {
  api: BaseQueryApi;
  error: unknown;
}): void => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  const state = api.getState() as { appStartup: AppStartupSliceState };
  if (
    !(error instanceof CredentialsManagerError) ||
    error.type !== CredentialsManagerErrorCodes.RENEW_FAILED ||
    !isNil(state.appStartup.auth0RenewFailure)
  ) {
    return;
  }

  // On native, renew failure is treated as fatal for this session: set the
  // `auth0RenewFailure` flag to trigger logout flow with restart-required fallback.
  //
  // 1. This function dispatches `setAuth0RenewFailureAction` with the serialized error
  // 2. `ErrorBoundaryProvider` detects the flag, logs the error, clears the flag, and calls `logout(LogoutReason.AuthError)`
  // 3. `logout()` navigates to `/logout` screen which runs cleanup via `useLoginInitialization`
  // 4. If cleanup fails, `/logout` screen shows `ErrorBoundaryUI` (restart required)
  //
  // We also intentionally do NOT clear RTK Query API state here. That can cause
  // currently-mounted subscribers to refetch, which both increases noise and can
  // unintentionally expose request/response details in state.
  api.dispatch(
    setAuth0RenewFailureAction({
      error: serializeAuthError(error).error,
      extra: {
        scenario: "baseQuery failed due to Auth0 Renew failure",
      },
    }),
  );
};

export type { SerializableAuthResponse };
export {
  Auth0SessionExpiredToastText,
  captureAuthErrorMessage,
  getBiometricsLabel,
  getFailedLoginReason,
  getUserFacingLoginErrorResponse,
  handleAuthError,
  handleUnrecoverableCredentialsApiCallErrorNative,
  handleUnrecoverableCredentialsApiCallErrorWeb,
  isCredentialsManagerErrorOfType,
  isUnrecoverableCredentialsErrorOnApiCall,
  isUnrecoverableCredentialsErrorOnAppInit,
  isUnrecoverableCredentialsErrorOnCheckCreds,
  serializeAuthError,
  wrapAuth0Call,
};
